import { useAtom, useAtomValue } from 'jotai'
import { motion } from 'motion/react'
import { LiveField, StatefulImage } from '../../rapid-ui'
import { useLogoUpload } from '../hooks/useLogoUpload'
import { DefaultAttribution } from '../simulator-tools/attribution'
import { EditableComplexText } from '../simulator-tools/editable'
import {
	campaignDetailsBrandingAtom,
	storyAdCtaTextAtom,
	storyAdLocationAtom,
	storyAdMetaSubtitleTextLayerAtom,
	storyAdMetaSubtitleTextTransformAtom,
	storyAdMetaTitleTextLayerAtom,
	storyAdMetaTitleTextTransformAtom,
	storyAdSubtitleAtom,
	storyAdTitleAtom,
} from '../state'

type CommonContentProps = {
	isGeneratingText: boolean
}

export function ContentLogo() {
	const activeBranding = useAtomValue(campaignDetailsBrandingAtom)
	const { triggerUpload, isUploading, fileInput } = useLogoUpload()

	return (
		<motion.div layoutId="story-ad-logo">
			<motion.div
				layout
				className="bg-white rounded-xl size-20 p-2 z-50"
				style={{
					backgroundColor: 'var(--logo-background-fill-color)',
				}}
			>
				<StatefulImage
					src={activeBranding?.logo?.url ?? ''}
					alt="Logo"
					className="h-full w-full cursor-pointer"
					style={{ backgroundColor: 'transparent' }}
					objectFit="contain"
					onClick={triggerUpload}
					forceIsLoading={isUploading}
				/>
			</motion.div>
			{fileInput}
		</motion.div>
	)
}

export function ContentLocation({ isGeneratingText }: CommonContentProps) {
	const [location, setLocation] = useAtom(storyAdLocationAtom)

	return (
		<motion.div layoutId="story-ad-location">
			<motion.div layout className="relative z-50 text-lg flex items-center h-[max-content] gap-2">
				<svg
					xmlns="http://www.w3.org/2000/svg"
					viewBox="0 0 24 24"
					id="Maps-Pin-3--Streamline-Nova"
					height={16}
					width={16}
				>
					<path
						d="M12 0.91a9.011 9.011 0 0 0 -9 9c0 7.469 8.124 12.632 8.47 12.848l0.53 0.332 0.53 -0.332C12.876 22.542 21 17.379 21 9.91a9.011 9.011 0 0 0 -9 -9zm0 14a5 5 0 1 1 5 -5 5.006 5.006 0 0 1 -5 5z"
						fill="currentColor"
						strokeWidth={1}
					/>
				</svg>
				<LiveField
					isGenerating={isGeneratingText}
					value={location ?? ''}
					onChange={setLocation}
					style={{
						textShadow:
							'0 0 2px rgba(0, 0, 0, 0.7), 0 0 8px rgba(0, 0, 0, 0.3), 0 0 16px rgba(0, 0, 0, 0.6)',
					}}
				/>
			</motion.div>
		</motion.div>
	)
}

export function ContentTitle({ isGeneratingText }: CommonContentProps) {
	const [title, setTitle] = useAtom(storyAdTitleAtom)
	const [titleTextTransform, setTitleTextTransform] = useAtom(storyAdMetaTitleTextTransformAtom)
	const [titleTextLayer, setTitleTextLayer] = useAtom(storyAdMetaTitleTextLayerAtom)

	if (!titleTextTransform || !titleTextLayer) {
		return null
	}

	return (
		<EditableComplexText
			className="text-3xl"
			focusTargetId={{ target: 'story-ad(contentTitle)' }}
			transform={titleTextTransform}
			overrideValue={true}
			textLayer={titleTextLayer}
			value={title ?? ''}
			onChange={setTitle}
			isGenerating={isGeneratingText}
			onTransformChange={setTitleTextTransform}
			onTextLayerChange={setTitleTextLayer}
		/>
	)
}

export function ContentSubtitle({ isGeneratingText }: CommonContentProps) {
	const [subtitle, setSubtitle] = useAtom(storyAdSubtitleAtom)
	const [subtitleTextTransform, setSubtitleTextTransform] = useAtom(storyAdMetaSubtitleTextTransformAtom)
	const [subtitleTextLayer, setSubtitleTextLayer] = useAtom(storyAdMetaSubtitleTextLayerAtom)

	if (!subtitleTextTransform || !subtitleTextLayer) {
		return null
	}

	return (
		<EditableComplexText
			className="text-xl"
			focusTargetId={{ target: 'story-ad(contentSubtitle)' }}
			transform={subtitleTextTransform}
			overrideValue={true}
			textLayer={subtitleTextLayer}
			value={subtitle ?? ''}
			onChange={setSubtitle}
			isGenerating={isGeneratingText}
			onTransformChange={setSubtitleTextTransform}
			onTextLayerChange={setSubtitleTextLayer}
		/>
	)
}

export function ContentCta({ isGeneratingText }: CommonContentProps) {
	const [ctaText, setCtaText] = useAtom(storyAdCtaTextAtom)
	const activeBranding = useAtomValue(campaignDetailsBrandingAtom)

	return (
		<motion.div layoutId="story-ad-cta">
			<motion.div
				layout
				className="text-grey-600 py-3 text-lg px-6 rounded w-[max-content] transition-all z-50"
				style={{
					backgroundColor: activeBranding?.accentBackground,
					color: activeBranding?.accentForeground,
				}}
			>
				<LiveField
					isGenerating={isGeneratingText}
					value={ctaText ?? ''}
					onChange={setCtaText}
				/>
			</motion.div>
		</motion.div>
	)
}

export function ContentAttribution() {
	return (
		<motion.div
			layoutId="story-ad-attribution"
			className="relative flex justify-end z-50"
		>
			<DefaultAttribution />
		</motion.div>
	)
}
